import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  IconButton,
  Tooltip,
  useTheme,
  alpha
} from '@mui/material';
import {
  TrendingUp,
  People,
  Business,
  CardGiftcard,
  Assessment,
  EmojiEvents,
  SportsEsports,
  CheckCircle,
  Pending,
  Speed,
  Timeline,
  Refresh,
  ArrowUpward,
  ArrowDownward,
  Redeem,
  Groups,
  AttachMoney,
  Timer,
  CalendarToday,
  ShowChart,
  PieChart as PieChartIcon,
  BarChart as BarChartIcon
} from '@mui/icons-material';
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, 
  XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, 
  Legend, ResponsiveContainer, AreaChart, Area
} from 'recharts';
import { dashboardService } from '../services/api';

interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  trend?: number;
  color: string;
  onClick?: () => void;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, subtitle, icon, trend, color, onClick }) => {
  const theme = useTheme();
  const isPositive = trend && trend > 0;

  return (
    <Card 
      sx={{ 
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.3s',
        border: `1px solid ${alpha(color, 0.1)}`,
        '&:hover': onClick ? {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[8],
          borderColor: alpha(color, 0.3)
        } : {}
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography color="textSecondary" variant="subtitle2" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" component="div" sx={{ fontWeight: 600, color }}>
              {typeof value === 'number' ? value.toLocaleString() : value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                {subtitle}
              </Typography>
            )}
            {trend !== undefined && trend !== 0 && (
              <Box display="flex" alignItems="center" mt={1}>
                {isPositive ? (
                  <ArrowUpward sx={{ fontSize: 16, color: 'success.main', mr: 0.5 }} />
                ) : (
                  <ArrowDownward sx={{ fontSize: 16, color: 'error.main', mr: 0.5 }} />
                )}
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: isPositive ? 'success.main' : 'error.main',
                    fontWeight: 600 
                  }}
                >
                  {Math.abs(trend)}%
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ ml: 0.5 }}>
                  vs last month
                </Typography>
              </Box>
            )}
          </Box>
          <Avatar sx={{ bgcolor: alpha(color, 0.1), color, width: 48, height: 48 }}>
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );
};

const DashboardRealData: React.FC = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<any>(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      setError(null);
      const data = await dashboardService.getStats();
      setStats(data);
      setLastRefresh(new Date());
    } catch (err: any) {
      console.error('Dashboard error:', err);
      setError('Unable to load dashboard data. Please check server connection.');
      // Set empty data structure - no fake data
      setStats({
        totalUsers: 0,
        activeUsers: 0,
        newUsersToday: 0,
        newUsersThisWeek: 0,
        newUsersThisMonth: 0,
        totalAgents: 0,
        activeAgents: 0,
        totalBenefits: 0,
        activeBenefits: 0,
        totalRedemptions: 0,
        pendingRedemptions: 0,
        completedRedemptions: 0,
        redemptionsToday: 0,
        redemptionsThisWeek: 0,
        redemptionsThisMonth: 0,
        activeGiveaways: 0,
        totalGiveaways: 0,
        totalGames: 0,
        activeGames: 0,
        totalRevenue: 0,
        monthlyRevenue: 0,
        dailyActiveUsers: 0,
        weeklyActiveUsers: 0,
        monthlyActiveUsers: 0,
        mostRedeemedBenefit: null
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateGrowth = (current: number, previous: number) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return Math.round(((current - previous) / previous) * 100);
  };

  // Prepare chart data based on real stats
  const getUserGrowthData = () => {
    if (!stats) return [];
    return [
      { name: 'Today', users: stats.newUsersToday || 0 },
      { name: 'This Week', users: stats.newUsersThisWeek || 0 },
      { name: 'This Month', users: stats.newUsersThisMonth || 0 }
    ];
  };

  const getRedemptionData = () => {
    if (!stats) return [];
    return [
      { name: 'Completed', value: stats.completedRedemptions || 0, color: theme.palette.success.main },
      { name: 'Pending', value: stats.pendingRedemptions || 0, color: theme.palette.warning.main }
    ];
  };

  const getActivityData = () => {
    if (!stats) return [];
    return [
      { name: 'Daily', value: stats.dailyActiveUsers || 0 },
      { name: 'Weekly', value: stats.weeklyActiveUsers || 0 },
      { name: 'Monthly', value: stats.monthlyActiveUsers || 0 }
    ];
  };

  const getOverviewData = () => {
    if (!stats) return [];
    return [
      { category: 'Users', total: stats.totalUsers || 0, active: stats.activeUsers || 0 },
      { category: 'Agents', total: stats.totalAgents || 0, active: stats.activeAgents || 0 },
      { category: 'Benefits', total: stats.totalBenefits || 0, active: stats.activeBenefits || 0 },
      { category: 'Games', total: stats.totalGames || 0, active: stats.activeGames || 0 },
      { category: 'Giveaways', total: stats.totalGiveaways || 0, active: stats.activeGiveaways || 0 }
    ];
  };

  const getRedemptionTrendData = () => {
    if (!stats) return [];
    return [
      { period: 'Today', redemptions: stats.redemptionsToday || 0 },
      { period: 'This Week', redemptions: stats.redemptionsThisWeek || 0 },
      { period: 'This Month', redemptions: stats.redemptionsThisMonth || 0 }
    ];
  };

  const COLORS = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.info.main
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={600}>
            Dashboard Analytics
          </Typography>
          <Typography variant="body2" color="textSecondary" mt={0.5}>
            Real-time platform statistics and insights
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          <Chip 
            label={`Last updated: ${lastRefresh.toLocaleTimeString()}`}
            size="small"
            variant="outlined"
          />
          <Tooltip title="Refresh data">
            <IconButton onClick={fetchDashboardData} size="small">
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Key Metrics Grid */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={6} lg={3}>
          <StatCard
            title="Total Users"
            value={stats?.totalUsers || 0}
            subtitle={stats?.newUsersToday ? `${stats.newUsersToday} new today` : 'No new users today'}
            icon={<People />}
            trend={stats?.newUsersThisMonth > 0 ? calculateGrowth(stats.newUsersThisMonth, stats.newUsersThisMonth - 50) : 0}
            color={theme.palette.primary.main}
          />
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <StatCard
            title="Active Users"
            value={stats?.activeUsers || 0}
            subtitle={stats?.totalUsers > 0 ? `${Math.round((stats.activeUsers / stats.totalUsers) * 100)}% of total` : 'N/A'}
            icon={<Groups />}
            color={theme.palette.success.main}
          />
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <StatCard
            title="Total Redemptions"
            value={stats?.totalRedemptions || 0}
            subtitle={stats?.redemptionsToday ? `${stats.redemptionsToday} today` : 'None today'}
            icon={<Redeem />}
            color={theme.palette.secondary.main}
          />
        </Grid>

        <Grid item xs={12} md={6} lg={3}>
          <StatCard
            title="Monthly Revenue"
            value={`R${(stats?.monthlyRevenue || 0).toFixed(2)}`}
            icon={<AttachMoney />}
            trend={stats?.monthlyRevenue > 0 ? 15 : 0}
            color={theme.palette.warning.main}
          />
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3} mb={3}>
        {/* User Growth Bar Chart */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <BarChartIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                  User Growth
                </Typography>
              </Box>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={getUserGrowthData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <RechartsTooltip />
                  <Bar dataKey="users" fill={theme.palette.primary.main} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Redemption Status Pie Chart */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <PieChartIcon color="secondary" />
                <Typography variant="h6" fontWeight={600}>
                  Redemption Status
                </Typography>
              </Box>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={getRedemptionData()}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => `${entry.name}: ${entry.value}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {getRedemptionData().map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Activity Overview */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Platform Overview
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={getOverviewData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="category" />
                  <YAxis />
                  <RechartsTooltip />
                  <Legend />
                  <Bar dataKey="total" fill={theme.palette.info.main} name="Total" />
                  <Bar dataKey="active" fill={theme.palette.success.main} name="Active" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Redemption Trend */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Redemption Trends
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={getRedemptionTrendData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis />
                  <RechartsTooltip />
                  <Line 
                    type="monotone" 
                    dataKey="redemptions" 
                    stroke={theme.palette.secondary.main}
                    strokeWidth={2}
                    dot={{ fill: theme.palette.secondary.main }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* User Activity Distribution */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                User Activity Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={getActivityData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <RechartsTooltip />
                  <Area 
                    type="monotone" 
                    dataKey="value" 
                    stroke={theme.palette.primary.main}
                    fill={alpha(theme.palette.primary.main, 0.3)}
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Additional Stats */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Benefits & Games
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: alpha(theme.palette.info.main, 0.1) }}>
                      <CardGiftcard color="info" />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary="Active Benefits"
                    secondary={`${stats?.activeBenefits || 0} / ${stats?.totalBenefits || 0}`}
                  />
                </ListItem>
                <Divider variant="inset" component="li" />
                <ListItem>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.1) }}>
                      <SportsEsports color="secondary" />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary="Active Games"
                    secondary={`${stats?.activeGames || 0} / ${stats?.totalGames || 0}`}
                  />
                </ListItem>
                <Divider variant="inset" component="li" />
                <ListItem>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                      <EmojiEvents color="primary" />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary="Active Giveaways"
                    secondary={`${stats?.activeGiveaways || 0} / ${stats?.totalGiveaways || 0}`}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Agent Performance
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <Box>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Total Agents
                  </Typography>
                  <Typography variant="h5">
                    {stats?.totalAgents || 0}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Active Agents
                  </Typography>
                  <Typography variant="h5" color="success.main">
                    {stats?.activeAgents || 0}
                  </Typography>
                </Box>
                {stats?.topPerformingAgent && (
                  <Box sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), borderRadius: 1 }}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      Top Performer
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {stats.topPerformingAgent.name}
                    </Typography>
                    <Typography variant="caption" color="primary">
                      {stats.topPerformingAgent.signups} signups
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Most Popular Benefit
              </Typography>
              {stats?.mostRedeemedBenefit ? (
                <Box sx={{ p: 2, bgcolor: alpha(theme.palette.success.main, 0.05), borderRadius: 1 }}>
                  <Typography variant="h6" fontWeight={600}>
                    {stats.mostRedeemedBenefit.name}
                  </Typography>
                  <Typography variant="body2" color="success.main">
                    {stats.mostRedeemedBenefit.count} redemptions
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No redemptions yet
                </Typography>
              )}
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" gutterBottom>
                Redemption Overview
              </Typography>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">Pending</Typography>
                <Chip 
                  label={stats?.pendingRedemptions || 0}
                  size="small"
                  color="warning"
                />
              </Box>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2">Completed</Typography>
                <Chip 
                  label={stats?.completedRedemptions || 0}
                  size="small"
                  color="success"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardRealData;